import React, { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  Box,
  Card,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  Tabs,
  Tab,
  CircularProgress,
  Alert, AlertTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
} from "@mui/material";
import {
  VerifiedUser as IconShieldCheck,
  BarChart as IconChartBar,
  Lock as IconLock,
  Description as IconFileText,
  ReportProblem as IconAlertTriangle,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  ArrowBack,
  AddCircle as PlusCircle,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { ApiRequestFailed, authenticateAndFetchData, authenticateAndPostData, retryFn } from "@lib/apis";
import {
  WarningAmber as IconWarnTriangle,
  InfoOutlined as IconInfoCircle,
} from "@mui/icons-material";
import {
  ResponsiveContainer,
  LineChart,
  CartesianGrid,
  ReferenceLine,
  XAxis,
  YAxis,
  Tooltip,
  Line,
} from "recharts";
import PaginatedServerSideDataTable from "@components/PaginatedServerSideDataTable";
import { ColDef, ValueFormatterParams } from "ag-grid-community";
import { useSearchParams, useNavigate } from "react-router-dom";
import { urls } from "@routes";

interface PageData {
  status_code?: number;
  status_text?: string;
  is_mapped?: boolean;
  is_verified?: boolean;
  has_traffic_data?: boolean;
  email_subdomains?: string[];
  time_zone?: string;
  compliance_status?: any;
  spam_rate?: any;
  authentication?: any;
  encryption?: any;
  delivery_errors?: any[];
}

export default function AdminGooglePostmasterTab() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  
  const domain = searchParams.get("domain") || "";
  const integrationId = searchParams.get("integration_id") || "";

  const [activeSideTab, setActiveSideTab] = useState("compliance");
  const [days, setDays] = useState("7");
  const [selectedSubdomain, setSelectedSubdomain] = useState<string>("");
  const [isPolling, setIsPolling] = useState(false);

  // Fetch Postmaster traffic data with optional email subdomain selection
  const { data: responseData, isLoading, refetch } = useQuery({
    queryKey: ["adminGooglePostmaster", domain, integrationId, days, selectedSubdomain],
    queryFn: () => {
      let url = `/admin/google-postmaster-traffic-data/?domain=${domain}&integration_id=${integrationId}&days=${days}`;
      if (selectedSubdomain) {
        url += `&email_subdomain=${selectedSubdomain}`;
      }
      return authenticateAndFetchData(url);
    },
    gcTime: 0,
    retry: retryFn,
    refetchOnWindowFocus: false,
    enabled: !!domain && !!integrationId,
  });

  const pageData: PageData = responseData?.data || responseData || {};
  const isMapped = pageData?.is_mapped ?? true;
  const isVerified = pageData?.is_verified ?? true;
  const hasTrafficData = pageData?.has_traffic_data ?? false;
  const emailSubdomains = pageData?.email_subdomains || [];

  // Mutation to connect domain on-demand via + Connect Now button
  const connectMutation = useMutation({
    mutationKey: ["connectAdminGooglePostmaster", domain, integrationId],
    mutationFn: () => authenticateAndPostData(
      "/admin/connect-google-postmaster-domain/",
      { domain, integration_id: integrationId },
      "post"
    ),
    gcTime: 0,
    retry: retryFn,
    onSuccess: (res: any) => {
      enqueueSnackbar(res?.data?.message || "Domain connected successfully!", { variant: "success" });
      refetch();
    },
    onError: (error: any) => {
      console.error(error);
      enqueueSnackbar(error?.data?.message || "Failed to connect domain to Google Postmaster.", { variant: "error" });
    },
  });

  // Polling query for background refresh task status with auto-retry and token handling
  const { data: statusResponse } = useQuery({
    queryKey: ["adminPostmasterRefreshPolling", domain, integrationId],
    queryFn: () => authenticateAndFetchData(
      `/admin/check-postmaster-refresh-status/?domain=${domain}&integration_id=${integrationId}`
    ),
    gcTime: 0,
    retry: retryFn,
    refetchOnWindowFocus: false,
    refetchInterval: (query) => {
      const data = query.state.data?.data || query.state.data;
      if (isPolling && (!data || !data.success)) {
        return 4000;
      }
      return false;
    },
    enabled: isPolling && !!domain && !!integrationId,
  });

  // Watch for polling success state changes
  useEffect(() => {
    if (statusResponse) {
      const resData = statusResponse?.data || statusResponse;
      if (resData?.success && isPolling) {
        setIsPolling(false);
        enqueueSnackbar("Live Postmaster data refreshed successfully.", { variant: "success" });
        refetch();
      }
    }
  }, [statusResponse, isPolling, refetch, enqueueSnackbar]);

  const startPolling = () => {
    setIsPolling(true);
  };

  // Mutation to trigger background live refresh from Google API (120 days sync)
  const refreshMutation = useMutation({
    mutationKey: ["refreshAdminGooglePostmaster"],
    mutationFn: () => authenticateAndPostData(
      "/admin/refresh-google-postmaster-data/", 
      { domain, integration_id: integrationId }, 
      "post"
    ),
    gcTime: 0,
    retry: retryFn,
    onSuccess: () => {
      enqueueSnackbar("Syncing 120 days of data in background...", { variant: "success" });
      startPolling();
    },
    onError: (error: any) => {
      console.error(error);
      enqueueSnackbar(error?.data?.message || "Failed to refresh Postmaster data.", { variant: "error" });
    }
  });

  const isRefreshing = refreshMutation.isPending || isPolling;

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 3, p: 3 }}>
      {/* Top Bar: Header, Refresh Button first, Subdomain Dropdown second, Timeframe Dropdown third */}
      <Card 
        variant="outlined" 
        sx={{ 
          p: 2.5, 
          display: "flex", 
          flexDirection: { xs: "column", sm: "row" }, 
          justifyContent: "space-between", 
          alignItems: { xs: "flex-start", sm: "center" }, 
          gap: 2,
          borderRadius: 2
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <IconButton
            onClick={() => navigate(`${urls["adminGooglePostmasterPage"]}?integration_id=${integrationId}`)}
            size="small"
            title="Back to Domains"
          >
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              Google Postmaster Metrics
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Displaying deliverability and compliance stats for <Box component="span" sx={{ fontWeight: 500, color: "text.primary" }}>{selectedSubdomain || domain || "domain"}</Box>
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2, flexShrink: 0 }}>
          
          {/* 1. Refresh Live Data Button First */}
          {isMapped && isVerified && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon className={isRefreshing ? "animate-spin" : ""} sx={{ fontSize: 16 }} />}
              onClick={() => refreshMutation.mutate()}
              disabled={isRefreshing}
              sx={{ textTransform: "none", fontSize: "0.75rem", height: 36 }}
            >
              {isRefreshing ? "Syncing 120 Days..." : "Refresh Live Data"}
            </Button>
          )}

          {/* 2. Email Subdomain Dropdown Selector Second */}
          {emailSubdomains.length > 0 && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={500}>
                  Domain:
                </Typography>

                <FormControl size="small" sx={{ width: 240 }}>
                  <Select
                    value={selectedSubdomain || domain}
                    onChange={(e) => setSelectedSubdomain(e.target.value)}
                    sx={{ fontSize: "0.75rem", height: 36 }}
                  >
                    <MenuItem value={domain} sx={{ fontSize: "0.75rem" }}>
                      Main ({domain})
                    </MenuItem>

                    {emailSubdomains.map((sub: string) => (
                      <MenuItem key={sub} value={sub} sx={{ fontSize: "0.75rem" }}>
                        {sub}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
          )}

          {/* 3. Timeframe Selector Dropdown Third */}
          {activeSideTab !== "compliance" && isMapped && isVerified && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={500}>
                Timeframe:
              </Typography>
              <FormControl size="small" sx={{ width: 140 }}>
                <Select
                  value={days}
                  onChange={(e) => setDays(e.target.value)}
                  sx={{ fontSize: "0.75rem", height: 36 }}
                >
                  <MenuItem value="7" sx={{ fontSize: "0.75rem" }}>Last 7 days</MenuItem>
                  <MenuItem value="30" sx={{ fontSize: "0.75rem" }}>Last 30 days</MenuItem>
                  <MenuItem value="90" sx={{ fontSize: "0.75rem" }}>Last 90 days</MenuItem>
                  <MenuItem value="120" sx={{ fontSize: "0.75rem" }}>Last 120 days</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}
        </Box>
      </Card>

      {/* Main Container Layout */}
      <Box sx={{ display: "flex", flexDirection: { xs: "column", lg: "row" }, gap: 3, alignItems: "flex-start" }}>
        
        {/* Left Vertical Sidebar Tabs */}
        <Box sx={{ flexShrink: "0", width: { xs: "100%", lg: 240 } }}>
          <Tabs
            orientation="vertical"
            value={activeSideTab}
            onChange={(_, val) => setActiveSideTab(val)}
            variant="scrollable"
            sx={{
              borderRight: 0,
              "& .MuiTab-root": {
                textTransform: "none",
                justifyContent: "flex-start",
                alignItems: "center",
                gap: 1.5,
                minHeight: 40,
                borderRadius: 1,
                mb: 0.5,
                px: 2,
                fontSize: "0.875rem",
                color: "text.secondary",
                "&.Mui-selected": {
                  color: "text.primary",
                  backgroundColor: "action.selected",
                  fontWeight: 600,
                },
              },
              "& .MuiTabs-indicator": {
                display: "none",
              },
            }}
          >
            <Tab value="compliance" label="Compliance Status" icon={<IconShieldCheck sx={{ fontSize: 18 }} />} iconPosition="start" />
            <Tab value="spam" label="Spam Rate" icon={<IconChartBar sx={{ fontSize: 18 }} />} iconPosition="start" />
            <Tab value="authentication" label="Authentication" icon={<IconLock sx={{ fontSize: 18 }} />} iconPosition="start" />
            <Tab value="encryption" label="Encryption" icon={<IconFileText sx={{ fontSize: 18 }} />} iconPosition="start" />
            <Tab value="delivery-error" label="Delivery Error" icon={<IconAlertTriangle sx={{ fontSize: 18 }} />} iconPosition="start" />
          </Tabs>
        </Box>

        {/* Right Main Content Area */}
        <Box sx={{ flex: 1, width: "100%" }}>
          {isLoading ? (
            <Card variant="outlined" sx={{ p: 6, textAlign: "center", borderRadius: 2 }}>
              <CircularProgress size={24} sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Fetching Google Postmaster metrics...
              </Typography>
            </Card>
          ) : !isMapped ? (
            <Card variant="outlined" sx={{ p: 6, textAlign: "center", borderRadius: 2, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
              <Box sx={{ maxWidth: 400 }}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Not Connected to Google Postmaster
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This domain and its email subdomains are not yet connected to Google Postmaster Tools. Click below to register and verify automatically.
                </Typography>
              </Box>
              <Button
                variant="contained"
                size="small"
                startIcon={<PlusCircle sx={{ fontSize: 18, ...(connectMutation.isPending && { animation: "spin 1s linear infinite" }) }} />}
                onClick={() => connectMutation.mutate()}
                disabled={connectMutation.isPending}
                sx={{ textTransform: "none", fontSize: "0.75rem", px: 3, py: 1 }}
              >
                {connectMutation.isPending ? "Connecting..." : "Connect Now"}
              </Button>
            </Card>
          ) : !isVerified ? (
            <UnverifiedStateBanner domain={domain || "your domain"} />
          ) : !hasTrafficData && activeSideTab !== "compliance" ? (
            <NotEnoughDataState />
          ) : (
            <Box>
              {activeSideTab === "compliance" && <ComplianceStatusView compliance={pageData?.compliance_status} />}
              {activeSideTab === "spam" && <AdminSpamRateView data={pageData?.spam_rate} />}
              {activeSideTab === "authentication" && <AdminAuthenticationView data={pageData?.authentication} />}
              {activeSideTab === "encryption" && <AdminEncryptionView data={pageData?.encryption} />}
              {activeSideTab === "delivery-error" && <AdminDeliveryErrorView errors={pageData?.delivery_errors} />}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export function UnverifiedStateBanner({ domain }: { domain: string }) {
  return (
    <Card variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
      <Alert
        severity="warning"
        icon={<IconWarnTriangle fontSize="small" />}
        sx={{
          borderRadius: 1.5,
          alignItems: "center",
          "& .MuiAlert-message": {
            fontSize: "0.875rem",
          },
        }}
      >
        Domain{" "}
        <Box component="span" sx={{ fontWeight: 600 }}>
          {domain}
        </Box>{" "}
        is pending DNS verification in Google Postmaster Tools. Verification runs
        automatically every 10 minutes.
      </Alert>
    </Card>
  );
}

// 2. MUI Version of NotEnoughDataState
export function NotEnoughDataState() {
  return (
    <Card
      variant="outlined"
      sx={{
        p: 5,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        borderRadius: 2,
        gap: 1.5,
      }}
    >
      <Box
        sx={{
          p: 1.5,
          borderRadius: "50%",
          backgroundColor: "action.hover",
          color: "text.secondary",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <IconInfoCircle sx={{ fontSize: 28 }} />
      </Box>

      <Typography variant="h6" fontWeight={600} sx={{ fontSize: "1.125rem" }}>
        Not enough outgoing email
      </Typography>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ maxWidth: 440, lineHeight: 1.5 }}
      >
        You haven't sent enough email to personal Gmail accounts yet to
        determine deliverability status for your domain. Metrics will populate
        automatically once traffic increases.
      </Typography>
    </Card>
  );
}

export function ComplianceStatusView({ compliance }: { compliance: any }) {
  const requirements = [
    { name: "SPF and DKIM authentication", key: "spf_and_dkim" },
    { name: "From: header alignment", key: "header_alignment" },
    { name: "DMARC authentication", key: "dmarc" },
    { name: "Encryption", key: "encryption" },
    { name: "User-reported spam rate", key: "spam_rate_compliant" },
    { name: "DNS records", key: "dns_records" },
    { name: "One-click unsubscribe", key: "one_click_unsub" },
    { name: "Honor unsubscribe", key: "honor_unsubscribe" },
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      {/* Header Description */}
      <Box>
        <Typography variant="h6" fontWeight={600} color="text.primary">
          Compliance status
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          This dashboard shows email sender requirements compliance for your domain and subdomains.
        </Typography>
      </Box>

      {/* Compliance Table Card */}
      <TableContainer component={Card} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table>
          <TableHead sx={{ backgroundColor: "action.hover" }}>
            <TableRow>
              <TableCell sx={{ fontSize: "0.75rem", fontWeight: 700, color: "text.secondary", textTransform: "uppercase" }}>
                Requirement
              </TableCell>
              <TableCell align="right" sx={{ fontSize: "0.75rem", fontWeight: 700, color: "text.secondary", textTransform: "uppercase" }}>
                Status
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {requirements.map((req, idx) => {
              const isCompliant = compliance?.[req.key] ?? false;

              return (
                <TableRow
                  key={idx}
                  hover
                  sx={{
                    "&:last-child td, &:last-child th": { border: 0 },
                  }}
                >
                  <TableCell component="th" scope="row" sx={{ fontSize: "0.875rem", fontWeight: 500 }}>
                    {req.name}
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
                      {isCompliant ? (
                        <>
                          <CheckCircleIcon color="success" sx={{ fontSize: 18 }} />
                          <Typography variant="body2" fontWeight={600} color="success.main">
                            Compliant
                          </Typography>
                        </>
                      ) : (
                        <>
                          <CancelIcon color="warning" sx={{ fontSize: 18 }} />
                          <Typography variant="body2" fontWeight={600} color="warning.main">
                            Needs Work
                          </Typography>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

interface SpamHistoryItem {
  date: string;
  rate: number; // e.g. 0.0015 for 0.15%
}

export function AdminSpamRateView({ data }: { data: any }) {
    const history: SpamHistoryItem[] = data?.history || [];
    
    // Pagination state aligned with server-side/client-side handling
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchQuery, setSearchQuery] = useState("");

    const chartData = history.map((item) => ({
        date: item.date,
        spamRate: Number(((item.rate ?? 0) * 100).toFixed(3)),
        rawRate: item.rate ?? 0,
    })).reverse();

    // Client-side pagination slicing for the history array
    const paginatedData = history.slice(
        (page - 1) * rowsPerPage,
        page * rowsPerPage
    );

    const handlePageChange = (newPage: number) => {
        setPage(newPage);
    };

    const handlePageSizeChange = (newSize: number) => {
        setRowsPerPage(newSize);
        setPage(1);
    };

    const handleSearchChange = (query: string) => {
      setSearchQuery(query);
      setPage(1);
    };
    
    // AG-Grid column definitions tailored for Spam History data
    const spamTableColumns: ColDef[] = [
        {
        field: "date",
        headerName: "Date",
        flex: 1,
        minWidth: 150,
        cellRenderer: (params: any) => (
            <div style={{ fontWeight: 500, display: "flex", alignItems: "center", height: "100%" }}>
            {params.value}
            </div>
        ),
        },
        {
        field: "rate",
        headerName: "User-reported spam rate",
        flex: 1,
        minWidth: 200,
        cellRenderer: (params: any) => {
            const rate = params.value ?? 0;
            const formattedRate = `${(rate * 100).toFixed(2)}%`;
            return (
            <div style={{ fontWeight: 500, display: "flex", alignItems: "center", height: "100%" }}>
                {formattedRate}
            </div>
            );
        },
        },
        {
        field: "under_threshold",
        headerName: "Under 0.3%",
        flex: 1,
        minWidth: 150,
        cellRenderer: (params: any) => {
            const rate = params.data?.rate ?? 0;
            const isUnderLimit = rate < 0.003;

            return (
            <div style={{ fontWeight: 500, display: "flex", alignItems: "center", height: "100%" }}>
                {isUnderLimit ? (
                <span style={{ color: "#2e7d32" }}>Yes</span>
                ) : (
                <span style={{ color: "#d32f2f" }}>No (Violation)</span>
                )}
            </div>
            );
        },
        },
    ];

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pb: "2rem" }}>
        {/* Title & Description */}
        <Box>
            <Typography variant="h6" fontWeight={600} color="text.primary">
            Spam
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            This dashboard shows the percentage of user-reported spam vs emails that were sent to the inbox for active users.
            </Typography>
        </Box>

        {/* Chart Card */}
        <Card variant="outlined" sx={{ p: 3, borderRadius: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Legend & Current Rate */}
            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 2, pb: 2, borderBottom: 1, borderColor: "divider" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 3, flexWrap: "wrap" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box component="span" sx={{ width: 12, height: 2, bgcolor: "#3b82f6", borderRadius: 1 }} />
                <Typography variant="caption" color="text.secondary">User-reported spam rate</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box component="span" sx={{ width: 12, height: 2, bgcolor: "#ef4444", borderRadius: 1 }} />
                <Typography variant="caption" color="text.secondary">Policy violation (0.3%)</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box component="span" sx={{ width: 12, height: 2, bgcolor: "#f59e0b", borderRadius: 1 }} />
                <Typography variant="caption" color="text.secondary">Recommended threshold (0.1%)</Typography>
                </Box>
            </Box>
            <Typography variant="body2" fontWeight={600} color="text.primary">
                Current: {data?.current || "0.00%"}
            </Typography>
            </Box>

            {/* Recharts Line Chart */}
            <Box sx={{ height: 280, width: "100%", pt: 1 }}>
            {chartData.length === 0 ? (
                <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Typography variant="body2" color="text.secondary">
                    No chart data available for the selected period.
                </Typography>
                </Box>
            ) : (
                <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                    <CartesianGrid vertical={false} stroke="currentColor" strokeOpacity={0.13} strokeDasharray="8 3" />
                    <ReferenceLine y={0} stroke="currentColor" strokeOpacity={0.2} strokeWidth={1} />
                    <XAxis dataKey="date" stroke="#888888" fontSize={12} tickLine={false} axisLine={false} />
                    <YAxis stroke="#888888" fontSize={12} tickLine={false} axisLine={false} domain={[0, 0.35]} tickFormatter={(val) => `${val}%`} />
                    <Tooltip 
                    contentStyle={{ 
                        backgroundColor: "background.paper", 
                        borderColor: "divider",
                        borderRadius: "6px",
                        fontSize: "12px",
                        color: "text.primary"
                    }}
                    formatter={(value: any) => [`${value}%`, "Spam Rate"]}
                    />
                    <ReferenceLine y={0.3} stroke="#ef4444" strokeWidth={1.5} strokeDasharray="4 4" />
                    <ReferenceLine y={0.1} stroke="#f59e0b" strokeWidth={1.5} strokeDasharray="4 4" />
                    <Line type="monotone" dataKey="spamRate" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3, fill: "#3b82f6" }} activeDot={{ r: 5 }} />
                </LineChart>
                </ResponsiveContainer>
            )}
            </Box>
        </Card>

        {/* AG-Grid / PaginatedServerSideDataTable Component */}
        <Box sx={{ height: 400, width: "100%", overflow: "hidden" }}>
            <PaginatedServerSideDataTable
                columns={spamTableColumns}
                paginationData={{
                    data: paginatedData,
                    total_count: history.length,
                    total_pages: Math.ceil(history.length / rowsPerPage) || 1,
                    current_page: page,
                    page_size: rowsPerPage,
                }}
                loading={false}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                onSearchChange={handleSearchChange}
                searchPlaceholder="Search..."
            />
        </Box>
        </Box>
    );
}

interface AuthHistoryItem {
  date: string;
  spf: number;
  dkim: number;
  dmarc: number;
}

export function AdminAuthenticationView({ data }: { data: any }) {
  const history: AuthHistoryItem[] = data?.history || [];
  
  // Pagination state for the server-side/client-side data table wrapper
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");

  const chartData = history.map((item) => ({
    date: item.date,
    spf: Number(((item.spf ?? 0) * 100).toFixed(2)),
    dkim: Number(((item.dkim ?? 0) * 100).toFixed(2)),
    dmarc: Number(((item.dmarc ?? 0) * 100).toFixed(2)),
  })).reverse();

  // Filter based on search query if needed, or slice for pagination
  const filteredHistory = history.filter((item) => 
    item.date.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const paginatedData = filteredHistory.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1);
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    setPage(1);
  };

  // AG-Grid Column Definitions configured for Authentication History View
  const columnDefs: ColDef[] = [
    {
      field: "date",
      headerName: "Date",
      flex: 1,
      minWidth: 150,
      cellRenderer: (params: any) => (
        <div style={{ fontWeight: 500, display: "flex", alignItems: "center", height: "100%" }}>
          {params.value}
        </div>
      ),
    },
    {
      field: "dkim",
      headerName: "DKIM Success Rate",
      flex: 1,
      minWidth: 180,
      cellRenderer: (params: any) => {
        const val = params.value ?? 0;
        return <span>{(val * 100).toFixed(2)}%</span>;
      },
    },
    {
      field: "spf",
      headerName: "SPF Success Rate",
      flex: 1,
      minWidth: 180,
      cellRenderer: (params: any) => {
        const val = params.value ?? 0;
        return <span>{(val * 100).toFixed(2)}%</span>;
      },
    },
    {
      field: "dmarc",
      headerName: "DMARC Success Rate",
      flex: 1,
      minWidth: 180,
      cellRenderer: (params: any) => {
        const val = params.value ?? 0;
        return <span>{(val * 100).toFixed(2)}%</span>;
      },
    },
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pb: "2rem" }}>
      {/* Title & Description */}
      <Box>
        <Typography variant="h6" fontWeight={600} color="text.primary">
          Authentication
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          This dashboard shows traffic that passed SPF, DKIM &amp; DMARC, over all received traffic that attempted authentication.
        </Typography>
      </Box>

      {/* Chart Card */}
      <Card variant="outlined" sx={{ p: 3, borderRadius: 2, display: "flex", flexDirection: "column", gap: 2 }}>
        {/* Legend */}
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 2, pb: 2, borderBottom: 1, borderColor: "divider" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 3, flexWrap: "wrap" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box component="span" sx={{ width: 12, height: 2, bgcolor: "#3b82f6", borderRadius: 1 }} />
              <Typography variant="caption" color="text.secondary">DMARC</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box component="span" sx={{ width: 12, height: 2, bgcolor: "#ef4444", borderRadius: 1 }} />
              <Typography variant="caption" color="text.secondary">DKIM</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box component="span" sx={{ width: 12, height: 2, bgcolor: "#f59e0b", borderRadius: 1 }} />
              <Typography variant="caption" color="text.secondary">SPF</Typography>
            </Box>
          </Box>
        </Box>

        {/* Recharts Line Chart */}
        <Box sx={{ height: 280, width: "100%", pt: 1 }}>
          {chartData.length === 0 ? (
            <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Typography variant="body2" color="text.secondary">
                No chart data available for the selected period.
              </Typography>
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke="currentColor" strokeOpacity={0.13} strokeDasharray="8 3" />
                <ReferenceLine y={0} stroke="currentColor" strokeOpacity={0.2} strokeWidth={1} />
                <XAxis dataKey="date" stroke="#888888" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="#888888" fontSize={12} tickLine={false} axisLine={false} domain={[0, 100]} tickFormatter={(val) => `${val}%`} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: "background.paper", 
                    borderColor: "divider",
                    borderRadius: "6px",
                    fontSize: "12px",
                    color: "text.primary"
                  }}
                  formatter={(value: any, name: any) => [`${value}%`, name.toUpperCase()]}
                />
                <Line type="monotone" dataKey="dmarc" name="DMARC" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3, fill: "#3b82f6" }} />
                <Line type="monotone" dataKey="dkim" name="DKIM" stroke="#ef4444" strokeWidth={2} dot={{ r: 3, fill: "#ef4444" }} />
                <Line type="monotone" dataKey="spf" name="SPF" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3, fill: "#f59e0b" }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Box>
      </Card>

      {/* Replaced Material Table with PaginatedServerSideDataTable */}
      <Box sx={{ height: 400, width: "100%", overflow: "hidden" }}>
        <PaginatedServerSideDataTable
          columns={columnDefs}
          paginationData={{
            data: paginatedData,
            total_count: filteredHistory.length,
            total_pages: Math.ceil(filteredHistory.length / pageSize) || 1,
            current_page: page,
            page_size: pageSize,
          }}
          loading={false}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onSearchChange={handleSearchChange}
          searchPlaceholder="Search by date"
        />
      </Box>
    </Box>
  );
}


interface EncryptionHistoryItem {
  date: string;
  inbound: number;
  outbound: number;
}

export function AdminEncryptionView({ data }: { data: any }) {
  const history: EncryptionHistoryItem[] = data?.history || [];
  
  // Pagination and search state for PaginatedServerSideDataTable
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");

  const chartData = history.map((item) => ({
    date: item.date,
    inbound: Number(((item.inbound ?? 0) * 100).toFixed(2)),
    outbound: Number(((item.outbound ?? 1) * 100).toFixed(2)),
  })).reverse();

  // Filter and paginate data locally for the table component
  const filteredHistory = history.filter((item) => 
    item.date.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const paginatedData = filteredHistory.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1);
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    setPage(1);
  };

  // AG-Grid Column Definitions configured for Encryption History View
  const columnDefs: ColDef[] = [
    {
      field: "date",
      headerName: "Date",
      flex: 1,
      minWidth: 150,
      cellRenderer: (params: any) => (
        <div style={{ fontWeight: 500, display: "flex", alignItems: "center", height: "100%" }}>
          {params.value}
        </div>
      ),
    },
    {
      field: "inbound",
      headerName: "Inbound TLS Rate",
      flex: 1,
      minWidth: 180,
      cellRenderer: (params: any) => {
        const val = params.value ?? 0;
        return <span>{(val * 100).toFixed(2)}%</span>;
      },
    },
    {
      field: "outbound",
      headerName: "Outbound TLS Rate",
      flex: 1,
      minWidth: 180,
      cellRenderer: (params: any) => {
        const val = params.value ?? 1;
        return <span>{(val * 100).toFixed(2)}%</span>;
      },
    },
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pb: "2rem", }}>
      {/* Title & Description */}
      <Box>
        <Typography variant="h6" fontWeight={600} color="text.primary">
          Encryption
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          This dashboard shows what percentage of your inbound and outbound traffic is encrypted.
        </Typography>
      </Box>

      {/* Chart Card */}
      <Card variant="outlined" sx={{ p: 3, borderRadius: 2, display: "flex", flexDirection: "column", gap: 2 }}>
        {/* Legend */}
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 2, pb: 2, borderBottom: 1, borderColor: "divider" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 3, flexWrap: "wrap" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box component="span" sx={{ width: 12, height: 2, bgcolor: "#3b82f6", borderRadius: 1 }} />
              <Typography variant="caption" color="text.secondary">Inbound TLS rate</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box component="span" sx={{ width: 12, height: 2, bgcolor: "#ef4444", borderRadius: 1 }} />
              <Typography variant="caption" color="text.secondary">Outbound TLS rate</Typography>
            </Box>
          </Box>
        </Box>

        {/* Recharts Line Chart */}
        <Box sx={{ height: 280, width: "100%", pt: 1 }}>
          {chartData.length === 0 ? (
            <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Typography variant="body2" color="text.secondary">
                No chart data available for the selected period.
              </Typography>
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke="currentColor" strokeOpacity={0.13} strokeDasharray="8 3" />
                <ReferenceLine y={0} stroke="currentColor" strokeOpacity={0.2} strokeWidth={1} />
                <XAxis dataKey="date" stroke="#888888" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="#888888" fontSize={12} tickLine={false} axisLine={false} domain={[0, 100]} tickFormatter={(val) => `${val}%`} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: "background.paper", 
                    borderColor: "divider",
                    borderRadius: "6px",
                    fontSize: "12px",
                    color: "text.primary"
                  }}
                  formatter={(value: any, name: any) => [`${value}%`, name]}
                />
                <Line type="monotone" dataKey="inbound" name="Inbound TLS rate" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3, fill: "#3b82f6" }} />
                <Line type="monotone" dataKey="outbound" name="Outbound TLS rate" stroke="#ef4444" strokeWidth={2} dot={{ r: 3, fill: "#ef4444" }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Box>
      </Card>

      {/* PaginatedServerSideDataTable Implementation */}
      <Box sx={{ height: 400, width: "100%", overflow: "hidden" }}>
        <PaginatedServerSideDataTable
          columns={columnDefs}
          paginationData={{
            data: paginatedData,
            total_count: filteredHistory.length,
            total_pages: Math.ceil(filteredHistory.length / pageSize) || 1,
            current_page: page,
            page_size: pageSize,
          }}
          loading={false}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onSearchChange={handleSearchChange}
          searchPlaceholder="Search by date"
        />
      </Box>
    </Box>
  );
}


interface DeliveryErrorRow {
  date: string;
  error_type: string;
  reason: string;
  ratio: number;
}

export function AdminDeliveryErrorView({ errors }: { errors: any }) {
  const chartHistory = errors?.history || [];
  const tableRows: DeliveryErrorRow[] = errors?.table_rows || (Array.isArray(errors) ? errors : []);
  
  // Pagination and search state for PaginatedServerSideDataTable
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");

  const chartData = chartHistory.map((item: any) => ({
    date: item.date,
    errorRate: Number(((item.rate ?? 0) * 100).toFixed(3)),
  })).reverse();

  // Filter table rows by search query (matching date, error type, or reason)
  const filteredRows = tableRows.filter((row) => 
    row.date.toLowerCase().includes(searchQuery.toLowerCase()) ||
    row.error_type.toLowerCase().includes(searchQuery.toLowerCase()) ||
    row.reason.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const paginatedData = filteredRows.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1);
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    setPage(1);
  };

  // AG-Grid Column Definitions configured for Delivery Errors View
  const columnDefs: ColDef[] = [
    {
      field: "date",
      headerName: "Date",
      flex: 1,
      minWidth: 140,
      cellRenderer: (params: any) => (
        <div style={{ fontWeight: 500, display: "flex", alignItems: "center", height: "100%" }}>
          {params.value}
        </div>
      ),
    },
    {
      field: "error_type",
      headerName: "Error Type",
      flex: 1,
      minWidth: 150,
      cellRenderer: (params: any) => (
        <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
          {params.value}
        </div>
      ),
    },
    {
      field: "reason",
      headerName: "Reason",
      flex: 2,
      minWidth: 250,
      cellRenderer: (params: any) => (
        <div style={{ display: "flex", alignItems: "center", height: "100%", color: "inherit", opacity: 0.85 }}>
          {params.value}
        </div>
      ),
    },
    {
      field: "ratio",
      headerName: "Percentage",
      flex: 1,
      minWidth: 130,
      cellRenderer: (params: any) => {
        const ratio = params.value ?? 0;
        return (
          <div style={{ display: "flex", alignItems: "center", height: "100%", fontWeight: 600, color: "#d32f2f" }}>
            {(ratio * 100).toFixed(2)}%
          </div>
        );
      },
    },
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pb: "2rem", }}>
      {/* Title & Description */}
      <Box>
        <Typography variant="h6" fontWeight={600} color="text.primary">
          Delivery Errors
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          This dashboard monitors what percentage of your total emails were rejected or temporarily failed as compared to all authenticated traffic.
        </Typography>
      </Box>

      {/* Chart Card */}
      <Card variant="outlined" sx={{ p: 3, borderRadius: 2, display: "flex", flexDirection: "column", gap: 2 }}>
        {/* Legend & Current Rate */}
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 2, pb: 2, borderBottom: 1, borderColor: "divider" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box component="span" sx={{ width: 12, height: 2, bgcolor: "#3b82f6", borderRadius: 1 }} />
            <Typography variant="caption" color="text.secondary">Delivery error rate</Typography>
          </Box>
          <Typography variant="body2" fontWeight={600} color="text.primary">
            Current: {errors?.current || "0.00%"}
          </Typography>
        </Box>

        {/* Recharts Line Chart */}
        <Box sx={{ height: 280, width: "100%", pt: 1 }}>
          {chartData.length === 0 ? (
            <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Typography variant="body2" color="text.secondary">
                No chart data available for the selected period.
              </Typography>
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke="currentColor" strokeOpacity={0.13} strokeDasharray="8 3" />
                <ReferenceLine y={0} stroke="currentColor" strokeOpacity={0.2} strokeWidth={1} />
                <XAxis dataKey="date" stroke="#888888" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="#888888" fontSize={12} tickLine={false} axisLine={false} domain={[0, 'auto']} tickFormatter={(val) => `${val}%`} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: "background.paper", 
                    borderColor: "divider",
                    borderRadius: "6px",
                    fontSize: "12px",
                    color: "text.primary"
                  }}
                  formatter={(value: any) => [`${value}%`, "Error Rate"]}
                />
                <Line type="monotone" dataKey="errorRate" name="Delivery error rate" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3, fill: "#3b82f6" }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Box>
      </Card>

      {/* PaginatedServerSideDataTable Implementation */}
      <Box sx={{ height: 400, width: "100%", overflow: "hidden" }}>
        <PaginatedServerSideDataTable
          columns={columnDefs}
          paginationData={{
            data: paginatedData,
            total_count: filteredRows.length,
            total_pages: Math.ceil(filteredRows.length / pageSize) || 1,
            current_page: page,
            page_size: pageSize,
          }}
          loading={false}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onSearchChange={handleSearchChange}
          searchPlaceholder="Search by date, error type, or reason"
        />
      </Box>
    </Box>
  );
}